import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Shield, Trophy, Heart } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

export const AboutSection = () => {
  const { t } = useLanguage();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const features = [
    { icon: Shield, title: t("about.f1t"), description: t("about.f1d") },
    { icon: Trophy, title: t("about.f2t"), description: t("about.f2d") },
    { icon: Heart, title: t("about.f3t"), description: t("about.f3d") },
  ];

  return (
    <section 
      id="about" 
      className="relative overflow-hidden isolate py-32 md:py-44 lg:py-52"
      ref={ref}
      data-testid="about-section"
    >
      {/* Full-width background image — kids playing football in different jerseys */}
      <div className="absolute inset-0 -z-10">
        <img
          src="https://images.unsplash.com/photo-1526232761682-d26e03ac148e?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
          alt={t("alts.aboutImg")}
          className="w-full h-full object-cover"
          data-testid="about-bg-image"
        />
        {/* Dark tint for text legibility — kept light so image stays visible */}
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/80" />
      </div>

      {/* Top border accent */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

      <div className="section-container">
        {/* Centered narrative block */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9 }}
          className="max-w-3xl mx-auto text-center"
        >
          {/* Label */}
          <p className="text-xs font-bold tracking-[0.4em] uppercase text-[#009A49] mb-6 font-body">
            {t("about.label")}
          </p>

          {/* Heading */}
          <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] mb-8 tracking-wide drop-shadow-[0_4px_20px_rgba(0,0,0,0.8)]">
            {t("about.heading1")}
            <br />
            <span className="text-zinc-300">{t("about.heading2")}</span>
          </h2>

          {/* Signature tagline */}
          <p className="font-heading text-[#009A49] text-2xl md:text-3xl tracking-[0.15em] uppercase mb-10 drop-shadow-[0_2px_10px_rgba(0,0,0,0.7)]" data-testid="about-tagline">
            {t("about.taglineLine1")}
            <br />
            <span className="text-white">{t("about.taglineLine2")}</span>
          </p>

          {/* Divider */}
          <div className="emerald-divider mx-auto mb-10" />

          {/* Description */}
          <p className="text-zinc-200 font-body text-lg md:text-xl leading-relaxed mb-6 drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)]">
            {t("about.p1")}
          </p>
          <p className="text-zinc-300 font-body text-base md:text-lg leading-relaxed drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)]">
            {t("about.p2")}
          </p>
        </motion.div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="grid md:grid-cols-3 gap-6 mt-20 md:mt-28"
        >
          {features.map((feature) => (
            <div 
              key={feature.title}
              className="p-8 border border-white/10 rounded-sm bg-black/70 backdrop-blur-md hover:border-[#009A49]/50 transition-colors duration-300"
            >
              <feature.icon className="w-8 h-8 text-[#009A49] mb-6" />
              <h3 className="font-heading text-xl font-semibold text-white mb-3">
                {feature.title}
              </h3>
              <p className="text-zinc-400 font-body text-sm leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
